import React, { useEffect, useState } from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Paper,
  Grid,
  Typography,
  useTheme
} from '@mui/material';
import { 
  TreePine, 
  Trash2, 
  Coins, 
  MapPin,
  Activity,
  Leaf,
  Thermometer,
  BarChart2
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { toast } from 'react-toastify';

import StatsCard from '../Components/UI/StatsCard';
import { ElevatedCard } from '../Components/UI/EnhancedCard';
import FullPageLoader from '../Components/UI/FullPageLoader';
import { ModernPieChart, ModernBarChart } from '../Components/Charts';

import { useOrg } from '../contexts/OrgContext';
import BASE_URL from '../config';
import {
  getHealthColor,
  getSpeciesColor,
  getIrrigationColor
} from '../utils/chartColors';

function DashboardModern() {
  const theme = useTheme();
  const { selectedOrgId } = useOrg();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [siteruns, setSiteruns] = useState([]);
  const [selectedSiterun, setSelectedSiterun] = useState(null);
  const [subSites, setSubSites] = useState([]);
  const [selectedSubSiteId, setSelectedSubSiteId] = useState('');
  const [timeseriesData, setTimeseriesData] = useState([]);

  const dashboardSeqRef = React.useRef(0);

  const handleShowDashboard = async () => {
    setLoading(true);
    const seq = ++dashboardSeqRef.current;
    try {
      const user = getAuth().currentUser;
      const token = user && (await user.getIdToken());
      const headers = { Authorization: `Bearer ${token}` };

      let query = `organisation_id=${selectedOrgId}&site_id=${selectedSiteId}`;
      if (selectedSubSiteId) query += `&sub_site_id=${selectedSubSiteId}`;
      if (selectedSiterun)   query += `&run_id=${selectedSiterun}`;

      let tsQuery = `site_id=${selectedSiteId}`;
      if (selectedSubSiteId) tsQuery += `&sub_site_id=${selectedSubSiteId}`;

      const [dashRes, tsRes] = await Promise.all([
        fetch(`${BASE_URL}/api/dashboard-data?${query}`, { headers }),
        fetch(`${BASE_URL}/api/site-metrics/timeseries?${tsQuery}`, { headers }),
      ]);

      if (!dashRes.ok) throw new Error(`HTTP error! Status: ${dashRes.status}`);
      const data = await dashRes.json();
      if (seq !== dashboardSeqRef.current) return;
      setDashboardData(data);

      if (tsRes.ok) {
        const tsData = await tsRes.json();
        if (seq === dashboardSeqRef.current) {
          setTimeseriesData(tsData.timeseries || []);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      if (seq === dashboardSeqRef.current) {
        setLoading(false);
      }
    }
  };

  // OPTIMIZED: Fetch sites with siteruns in a single call
  const fetchDashboardInit = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user && selectedOrgId) {
      setInitialLoading(true);
      const idToken = await user.getIdToken();
      try {
        const response = await axios.get(
          `${BASE_URL}/api/dashboard-init?organisation_id=${selectedOrgId}`,
          {
            headers: { Authorization: `Bearer ${idToken}` }
          }
        );
        
        const { sites: fetchedSites } = response.data;
        setSites(fetchedSites);
        
        // Auto-select first TM site
        const tmSites = fetchedSites.filter((site) => site.site_id.includes('TM'));
        if (tmSites.length > 0) {
          const firstSite = tmSites[0];
          setSelectedSite(firstSite.site_name);
          setSelectedSiteId(firstSite.site_id.toString());
          setSiteruns(firstSite.siteruns || []);
          setSubSites(firstSite.sub_sites || []);
          setSelectedSubSiteId('');
          
          if (firstSite.siteruns?.length > 0) {
            setSelectedSiterun(firstSite.siteruns[0].run_id);
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard init:', error);
        toast.error('Failed to fetch sites');
      } finally {
        setInitialLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedOrgId) {
      setSelectedSiteId('');
      setSelectedSite('');
      setSiteruns([]);
      setSelectedSiterun(null);
      setSubSites([]);
      setDashboardData(null);
      setTimeseriesData([]);
      fetchDashboardInit();
    }
  }, [selectedOrgId]);

  // Reload whenever site, sub-site, or run changes (and init is complete)
  useEffect(() => {
    if (selectedSiteId && !initialLoading) {
      handleShowDashboard();
    }
  }, [selectedSiteId, selectedSubSiteId, selectedSiterun, initialLoading]);

  const handleSiteChange = (e) => {
    const siteName = e.target.value;
    setSelectedSite(siteName);
    setDashboardData(null);
    setTimeseriesData([]);

    const selectedSiteObj = sites.find((site) => site.site_name === siteName);

    if (selectedSiteObj) {
      const id = selectedSiteObj.site_id.toString();
      setSelectedSiteId(id);
      setSubSites(selectedSiteObj.sub_sites || []);
      setSelectedSubSiteId('');
      // Use siteruns from the already-fetched site data
      setSiteruns(selectedSiteObj.siteruns || []);
      
      if (selectedSiteObj.siteruns?.length > 0) {
        setSelectedSiterun(selectedSiteObj.siteruns[0].run_id);
      } else {
        setSelectedSiterun(null);
      }
    } else {
      setSelectedSiteId('');
      setSiteruns([]);
      setSelectedSiterun(null);
    }
  };

  const formattedRuns = siteruns.map((run) => {
    const date = run.timestamp ? new Date(run.timestamp) : null;
    return {
      ...run,
      formattedTimestamp: date && !isNaN(date) ? date.toISOString().split('T')[0] : ''
    };
  });

  if ((loading || initialLoading) && !dashboardData) {
    return <FullPageLoader message="Loading dashboard data..." />;
  }

  return (
    <Box
      sx={{
        p: 3,
        height: '100%',
        overflow: 'auto',
        boxSizing: 'border-box',
        width: '100%'
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Activity size={24} color="#6BA368" />
              <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
                Ecological Dashboard
              </h1>
            </Box>
            <p style={{ margin: 0, color: '#757575', fontSize: '0.875rem' }}>
              Monitor tree health, species distribution, and site metrics
            </p>
          </Box>
        </Box>

        {/* Controls */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Site</InputLabel>
            <Select
              value={selectedSite}
              onChange={handleSiteChange}
              label="Site"
            >
              {sites
                .filter((site) => site.site_id.includes('TM'))
                .map((site) => (
                  <MenuItem key={site.site_id} value={site.site_name}>
                    {site.site_name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {subSites.length > 0 && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Sub-Site</InputLabel>
              <Select
                value={selectedSubSiteId}
                onChange={(e) => { setSelectedSubSiteId(e.target.value); setDashboardData(null); setTimeseriesData([]); }}
                label="Sub-Site"
              >
                <MenuItem value="">All Sub-Sites</MenuItem>
                {subSites.map((ss) => (
                  <MenuItem key={ss.sub_site_id} value={ss.sub_site_id}>
                    {ss.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Timestamp</InputLabel>
            <Select
              value={selectedSiterun || ''}
              onChange={(e) => { setSelectedSiterun(e.target.value); setDashboardData(null); }}
              label="Timestamp"
            >
              {formattedRuns.map((run) => (
                <MenuItem key={run.run_id} value={run.run_id}>
                  {run.formattedTimestamp}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Trees Planted"
            value={(dashboardData?.metrics?.treeCount ?? dashboardData?.metrics?.newTreesPlanted ?? 0).toLocaleString()}
            icon={<TreePine size={24} />}
            trend={{ value: 0, isPositive: true }}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Carbon Stock"
            value={dashboardData?.metrics?.carbonTc ? `${Number(dashboardData.metrics.carbonTc).toFixed(2)} tC` : '—'}
            icon={<Leaf size={24} />}
            trend={{ value: 0, isPositive: true }}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Natural Capital"
            value={
              dashboardData?.metrics?.naturalCapitalUsd
                ? `$${Number(dashboardData.metrics.naturalCapitalUsd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : dashboardData?.metrics?.naturalCapital
                  ? `$${Number(dashboardData.metrics.naturalCapital).toFixed(2)}`
                  : '—'
            }
            icon={<Coins size={24} />}
            trend={{ value: 0, isPositive: true }}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Site Area"
            value={dashboardData?.metrics?.siteArea ? `${Number(dashboardData.metrics.siteArea).toFixed(2)} acres` : '—'}
            icon={<MapPin size={24} />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Greenness"
            value={
              dashboardData?.metrics?.greennessDelta != null
                ? (dashboardData.metrics.greennessDelta >= 0 ? '+' : '') + Number(dashboardData.metrics.greennessDelta).toFixed(3)
                : '—'
            }
            subtitle={
              dashboardData?.metrics?.greennessBaseline != null
                ? `${Number(dashboardData.metrics.greennessBaseline).toFixed(3)} → ${Number(dashboardData.metrics.greenness ?? 0).toFixed(3)}`
                : undefined
            }
            icon={<BarChart2 size={24} />}
            trend={dashboardData?.metrics?.greennessDelta != null ? {
              value: Math.abs(dashboardData.metrics.greennessDelta * 100).toFixed(1),
              isPositive: dashboardData.metrics.greennessDelta >= 0
            } : { value: 0, isPositive: true }}
            color={dashboardData?.metrics?.greennessDelta >= 0 ? 'success' : 'error'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Temperature Anomaly"
            value={
              dashboardData?.metrics?.tempAnomalyDelta != null
                ? (dashboardData.metrics.tempAnomalyDelta >= 0 ? '+' : '') + Number(dashboardData.metrics.tempAnomalyDelta).toFixed(3) + ' °C'
                : '—'
            }
            subtitle={
              dashboardData?.metrics?.tempAnomalyBaseline != null
                ? `${Number(dashboardData.metrics.tempAnomalyBaseline).toFixed(3)} → ${Number(dashboardData.metrics.tempAnomaly ?? 0).toFixed(3)}`
                : undefined
            }
            icon={<Thermometer size={24} />}
            trend={dashboardData?.metrics?.tempAnomalyDelta != null ? {
              value: Math.abs(dashboardData.metrics.tempAnomalyDelta).toFixed(3),
              isPositive: dashboardData.metrics.tempAnomalyDelta <= 0
            } : { value: 0, isPositive: true }}
            color={dashboardData?.metrics?.tempAnomalyDelta != null && dashboardData.metrics.tempAnomalyDelta < 0 ? 'success' : 'error'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Trees Removed"
            value={dashboardData?.metrics?.treesRemoved ?? '0'}
            icon={<Trash2 size={24} />}
            trend={{ value: 0, isPositive: false }}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Tree Health — pie chart */}
        <Grid item xs={12} md={4}>
          <ElevatedCard elevation={2} sx={{ height: '100%' }}>
            <Box sx={{ height: 380, p: 2, pt: 1 }}>
              <ModernPieChart
                title="Tree Health Distribution"
                subtitle="Count by health status"
                data={dashboardData?.treeHealth || []}
                height={300}
                colorMap={{
                  // Backend formatHealth() emits High/Moderate/Low/NA (keys lowercased by the chart)
                  'high': theme.palette.success.main,
                  'moderate': theme.palette.warning.main,
                  'low': theme.palette.error.main,
                  'na': theme.palette.grey[400]
                }}
              />
            </Box>
          </ElevatedCard>
        </Grid>

        {/* Greenness (NDVI) — last 3 recorded timestamps */}
        <Grid item xs={12} md={4}>
          <ElevatedCard elevation={2} sx={{ height: '100%' }}>
            <Box sx={{ height: 380, p: 2, pt: 1 }}>
              <ModernBarChart
                title="Greenness"
                subtitle="Anomaly index by observation date"
                data={timeseriesData.slice(-3).map(r => ({
                  label: String(r.recorded_at).slice(0, 10),
                  greenness: r.greenness != null ? Number(r.greenness) : null,
                }))}
                height={280}
                xAxisKey="label"
                bars={[
                  { dataKey: 'greenness', name: 'Vegetation Indicator', color: theme.palette.success.main },
                ]}
              />
            </Box>
          </ElevatedCard>
        </Grid>

        {/* Temperature Anomaly — last 3 recorded timestamps */}
        <Grid item xs={12} md={4}>
          <ElevatedCard elevation={2} sx={{ height: '100%' }}>
            <Box sx={{ height: 380, p: 2, pt: 1 }}>
              <ModernBarChart
                title="Temperature Anomaly (°C)"
                subtitle="Anomaly vs baseline by observation date"
                data={timeseriesData.slice(-3).map(r => ({
                  label: String(r.recorded_at).slice(0, 10),
                  temp_anomaly: r.temp_anomaly != null ? Number(r.temp_anomaly) : null,
                }))}
                height={280}
                xAxisKey="label"
                bars={[
                  { dataKey: 'temp_anomaly', name: '°C', color: theme.palette.info.main },
                ]}
              />
            </Box>
          </ElevatedCard>
        </Grid>
      </Grid>

      {/* Monthly Health Chart */}
      <Paper
        elevation={2}
        sx={{ 
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper'
        }}
      >
        {dashboardData?.monthlyHealthChartData && dashboardData.monthlyHealthChartData.length > 0 ? (
          <ModernBarChart
            title="Tree Health Distribution Over Time"
            subtitle="Monthly tree health tracking across all categories"
            data={(() => {
              // Transform the data to the format needed by ModernBarChart
              const transformed = {};
              dashboardData.monthlyHealthChartData.forEach(item => {
                const key = `${item.month}-${item.year}`;
                if (!transformed[key]) {
                  transformed[key] = { label: key };
                }
                transformed[key][item.health_status] = parseInt(item.count);
              });
              return Object.values(transformed);
            })()}
            height={350}
            stacked={true}
            xAxisKey="label"
            bars={[
              { dataKey: 'high', name: 'High Health', color: theme.palette.success.main },
              { dataKey: 'medium', name: 'Medium Health', color: theme.palette.warning.main },
              { dataKey: 'low', name: 'Low Health', color: theme.palette.error.main }
            ]}
          />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: 350,
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              {loading ? 'Loading chart data...' : 'Select a site and timestamp to view health distribution'}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default DashboardModern;
